import { ApiError } from "./errors";

/** Default ceiling per request; past it the UI gets an error, not a spinner. */
export const DEFAULT_TIMEOUT_MS = 10_000;

export type QueryValue = string | number | boolean | undefined;
export type Query = Readonly<Record<string, QueryValue>>;
export type JsonBody = Readonly<Record<string, unknown>>;

export interface HttpOptions {
  /** Base URL of the backend, with or without a trailing slash. */
  readonly baseUrl: string;
  /** Returns the current bearer token, or null when signed out. */
  readonly getToken: () => string | null;
  /** Injected for tests; defaults to the global fetch. */
  readonly fetchImpl?: typeof fetch;
  readonly timeoutMs?: number;
}

export interface GetOptions {
  /** Defaults to true — nearly every route is per-user. */
  readonly auth?: boolean;
  readonly query?: Query;
  /** A longer ceiling for the few reads that may wait on a model. */
  readonly timeoutMs?: number;
}

export interface PostOptions {
  readonly auth?: boolean;
  /** A longer ceiling for the few calls that wait on a model. */
  readonly timeoutMs?: number;
}

/**
 * The thin transport every endpoint group is built on.
 *
 * Turns a path + body into a fetch, applies the token and the timeout, and
 * normalises every failure into an {@link ApiError}. Holds no state beyond
 * its configuration, so one instance serves the whole app.
 *
 * Every method is `async` so that even a failure found before the network
 * (no token) arrives as a rejection, never as a synchronous throw a caller
 * awaiting the promise would miss.
 */
export interface Http {
  get(path: string, options?: GetOptions): Promise<unknown>;
  post(path: string, body: JsonBody, options?: PostOptions): Promise<unknown>;
  put(path: string, body?: JsonBody): Promise<unknown>;
  del(path: string): Promise<unknown>;
}

export function createHttp(options: HttpOptions): Http {
  const baseUrl = options.baseUrl.replace(/\/+$/, "");
  const fetchImpl = options.fetchImpl ?? fetch;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  function headersFor(auth: boolean, json: boolean): Headers {
    const headers = new Headers({ Accept: "application/json" });
    if (json) headers.set("Content-Type", "application/json");
    if (auth) {
      const token = options.getToken();
      if (!token) throw ApiError.notAuthenticated();
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  }

  async function send(url: string, init: RequestInit, ceilingMs = timeoutMs): Promise<unknown> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ceilingMs);
    let response: Response;
    try {
      response = await fetchImpl(url, { ...init, signal: controller.signal });
    } catch {
      throw ApiError.network();
    } finally {
      clearTimeout(timer);
    }
    return decode(response);
  }

  return {
    async get(path, getOptions = {}) {
      const url = withQuery(`${baseUrl}${path}`, getOptions.query);
      const headers = headersFor(getOptions.auth ?? true, false);
      return send(url, { method: "GET", headers }, getOptions.timeoutMs ?? timeoutMs);
    },
    async post(path, body, postOptions = {}) {
      const headers = headersFor(postOptions.auth ?? true, true);
      return send(
        `${baseUrl}${path}`,
        { method: "POST", headers, body: JSON.stringify(body) },
        postOptions.timeoutMs ?? timeoutMs,
      );
    },
    async put(path, body = {}) {
      const headers = headersFor(true, true);
      return send(`${baseUrl}${path}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
      });
    },
    async del(path) {
      const headers = headersFor(true, false);
      return send(`${baseUrl}${path}`, { method: "DELETE", headers });
    },
  };
}

function withQuery(url: string, query?: Query): string {
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `${url}?${encoded}` : url;
}

/** Decodes a response body, throwing a typed error for any non-2xx status. */
async function decode(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  const parsed = parseJson(text);
  if (response.ok) return parsed;

  const detail = isRecord(parsed) ? parsed["detail"] : undefined;
  const code = typeof detail === "string" ? detail : `http_${response.status}`;
  throw ApiError.fromStatus(response.status, code);
}

function parseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
